import { useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  UPLOAD_SESSION_LIST_PAGE_SIZE,
  UPLOAD_SESSION_POLLING_INTERVAL_MS
} from 'src/config';
import SessionProgressCard from 'src/content/DataLibrary/UploadSession/Content/SessionProgressCard';
import { SessionStatus } from 'src/content/DataLibrary/UploadSession/enum';
import { useSessionWidget } from 'src/context/SessionWidgetContext';
import { useListUploadSessionsQuery } from 'src/newServices/uploadSessions';
import { TRANSLATION_CONSTANTS as T } from 'src/utils/translations';

function formatEta(seconds: number, t: (key: string, opts?: any) => string): string {
  if (seconds < 60) return t(T.etaSeconds, { count: Math.ceil(seconds) });
  const minutes = Math.ceil(seconds / 60);
  if (minutes < 60) return t(T.etaMinutes, { count: minutes });
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  if (remainingMinutes === 0) return t(T.etaHours, { count: hours });
  return t(T.etaHoursMinutes, { hours, minutes: remainingMinutes });
}

export default function GlobalSessionManager() {
  const { isMinimized, returnPath, maximizeSession, closeSession } =
    useSessionWidget();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const speedRef = useRef<{ prevBytes: number; prevTime: number; speed: number }>({
    prevBytes: 0,
    prevTime: 0,
    speed: 0
  });

  const { data: activeSessionsData } = useListUploadSessionsQuery(
    {
      params: {
        view: SessionStatus.ACTIVE,
        ps: UPLOAD_SESSION_LIST_PAGE_SIZE,
        p: 0
      }
    },
    { skip: !isMinimized, pollingInterval: UPLOAD_SESSION_POLLING_INTERVAL_MS }
  );

  const { activeSessionCount, progressPercent, totalBytes, uploadedBytes } =
    useMemo(() => {
      const rows =
        activeSessionsData?.data ?? activeSessionsData?.ret_val?.data ?? [];
      if (!Array.isArray(rows) || rows.length === 0) {
        return {
          activeSessionCount: 0,
          progressPercent: 0,
          totalBytes: 0,
          uploadedBytes: 0
        };
      }

      let total = 0;
      let uploaded = 0;
      for (const row of rows) {
        total += row.total_bytes ?? 0;
        uploaded += Number(row.uploaded_bytes) || 0;
      }

      const percent =
        total > 0 ? Math.round((uploaded / total) * 100) : 0;
      return {
        activeSessionCount: rows.length,
        progressPercent: percent,
        totalBytes: total,
        uploadedBytes: uploaded
      };
    }, [activeSessionsData]);

  useEffect(() => {
    // Once the widget is up and a real fetch confirms no active sessions are
    // left (e.g. the in-progress session was deleted/cancelled elsewhere),
    // close it instead of leaving a stale "0 sessions" drawer open.
    if (isMinimized && activeSessionsData && activeSessionCount === 0) {
      closeSession();
    }
  }, [isMinimized, activeSessionsData, activeSessionCount, closeSession]);

  useEffect(() => {
    const now = Date.now();
    const prev = speedRef.current;

    if (prev.prevTime > 0 && uploadedBytes > prev.prevBytes) {
      const elapsedSec = (now - prev.prevTime) / 1000;
      if (elapsedSec > 0) {
        const currentSpeed = (uploadedBytes - prev.prevBytes) / elapsedSec;
        speedRef.current.speed =
          prev.speed > 0 ? prev.speed * 0.3 + currentSpeed * 0.7 : currentSpeed;
      }
    }

    speedRef.current.prevBytes = uploadedBytes;
    speedRef.current.prevTime = now;
  }, [uploadedBytes]);

  const estimatedTimeRemaining = useMemo(() => {
    const remaining = totalBytes - uploadedBytes;
    const { speed } = speedRef.current;
    if (remaining <= 0 || speed <= 0) return undefined;
    const seconds = remaining / speed;
    return `~ ${formatEta(seconds, t)}`;
  }, [totalBytes, uploadedBytes, t]);

  if (!isMinimized) return null;

  const handleMaximize = () => {
    maximizeSession();
    if (returnPath) {
      navigate(returnPath);
    }
  };

  return (
    <div className="tw:fixed tw:bottom-l tw:right-l tw:w-80 tw:z-[1300]">
      <SessionProgressCard
        activeSessionCount={activeSessionCount}
        progressPercent={progressPercent}
        estimatedTimeRemaining={estimatedTimeRemaining}
        onMaximize={handleMaximize}
      />
    </div>
  );
}
